import type { ButtonProps } from '@openx/components-core/lib/Button/Button';
import type { UseConfirmationDialogParams } from '@openx/components-core/lib/Dialog/ConfirmationDialog/types';
import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog';
import { CuratedPackageAction, type PackageListItem } from '@openx/types/package';
import type { HasCuratedPermission } from '@openx/types/package/PackageHookTypes';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PackagesStatusDialogContent } from '../../components/PackagesList/PackagesStatusDialogContent';
import { PackageRowActionType } from '../../types';
import { usePackageContext } from '../context/PackageContext';
import { usePackageRowActionTranslate } from './usePackageRowActionTranslate';

interface GetActionModalParams {
  packages: PackageListItem[];
  isBulkMode?: boolean;
  onConfirmed(packages: PackageListItem[]): Promise<void>;
  action: PackageRowActionType;
  title: string;
  confirmationDialog: {
    open: (params: Partial<UseConfirmationDialogParams>) => void;
    close: () => void;
    setLoading: (loading: boolean) => void;
  };
  confirmationLabel: string;
  confirmationButtonColor?: ButtonProps['color'];
  hasCuratedPermission: HasCuratedPermission;
}

const actionModal = ({
  packages,
  isBulkMode = false,
  onConfirmed,
  action,
  title,
  confirmationDialog,
  confirmationLabel,
  confirmationButtonColor = 'error',
  hasCuratedPermission,
}: GetActionModalParams) => {
  const { open, close } = confirmationDialog;

  let filteredPackages = packages;

  if (action === PackageRowActionType.DELETE) {
    filteredPackages = packages.filter(
      pckg =>
        !pckg.is_curated || (pckg.is_curated && hasCuratedPermission({ action: CuratedPackageAction.DELETE, pckg }))
    );
  }

  open({
    confirmationButtonColor,
    confirmationLabel,
    content: (
      <PackagesStatusDialogContent
        packages={filteredPackages}
        allPackages={packages.length}
        action={action}
        isBulkMode={isBulkMode}
      />
    ),
    customContent: true,
    hideConfirm: filteredPackages.length === 0,
    onConfirmed: async () => {
      try {
        await onConfirmed(filteredPackages);
      } finally {
        close();
      }
    },
    showExtraBtn: false,
    title: title,
  });
};

type CallPackageAction = {
  action: PackageRowActionType;
  packages: PackageListItem[];
  isBulkMode?: boolean;
  skipModal?: boolean;
  onFinish?: () => void;
};

export const usePackageOperations = () => {
  const { t } = useTranslation();
  const confirmationDialog = useConfirmationDialog();
  const packageRowTranslate = usePackageRowActionTranslate();
  const { usePackagePermissions, usePackageOperationActions } = usePackageContext();
  const { hasCuratedPermission } = usePackagePermissions();

  const { clonePackages, deletePackages, onSuccessfullAction } = usePackageOperationActions();

  const [loadingAction, setLoadingAction] = useState<PackageRowActionType | null>(null);

  const setLoading = useCallback(
    (action: PackageRowActionType | null) => {
      setLoadingAction(action);
      confirmationDialog.setLoading(!!action);
    },
    [confirmationDialog]
  );

  const actions = useMemo(
    () => ({
      [PackageRowActionType.DELETE]: deletePackages,
      [PackageRowActionType.CLONE]: clonePackages,
    }),
    [clonePackages, deletePackages]
  );

  const callPackageAction = useCallback(
    ({ action, packages, isBulkMode = false, skipModal = false, onFinish }: CallPackageAction) => {
      const onConfirmed = async (packages: PackageListItem[]) => {
        try {
          setLoading(action);
          await actions[action](packages);
          onSuccessfullAction?.(isBulkMode);
        } catch (e) {
          // biome-ignore lint/suspicious/noConsole: logging error in console
          console.error('error while requesting action', action, e);
        } finally {
          setLoading(null);
          onFinish?.();
        }
      };

      if (skipModal) {
        return onConfirmed(packages);
      }

      const packageLabel = isBulkMode ? t('Packages') : t('Package');
      const confirmationButtonColor = action === PackageRowActionType.CLONE ? 'primary' : 'error';

      actionModal({
        action,
        confirmationButtonColor,
        confirmationDialog,
        confirmationLabel: packageRowTranslate[action].toLowerCase(),
        hasCuratedPermission,
        isBulkMode,
        onConfirmed: onConfirmed,
        packages,
        title: t('{action} {package}', {
          action: packageRowTranslate[action],
          package: packageLabel,
        }),
      });
    },
    [actions, confirmationDialog, hasCuratedPermission, onSuccessfullAction, packageRowTranslate, setLoading, t]
  );

  return { callPackageAction, loadingAction };
};
